import { isPrivateGcpFileUrl } from "../../services/gcpFileUrl";
import type { SourceItem } from "../ai/sourceItems";

export type CitationSourceRoute =
  | "table"
  | "contentWithPdf"
  | "privatePdf"
  | "privateDocument"
  | "iframeHtml"
  | "html"
  | "markdown";

function normalize(value: string | null | undefined): string {
  return String(value || "").trim().toLowerCase();
}

function isPdfLikeUrl(url: string | null | undefined): boolean {
  return PDF_URL_RE.test(String(url || ""));
}

function routeContent(
  routeInfo: Omit<CitationRouteInfo, "route">
): CitationRouteInfo {
  return {
    ...routeInfo,
    route: routeInfo.hasStructuredHtml ? "html" : "markdown",
  };
}

function isChatContentFirstSource(dataSource: string, client: string): boolean {
  return (
    CHAT_CONTENT_FIRST_DATA_SOURCES.has(dataSource) ||
    CHAT_CONTENT_FIRST_CLIENTS.has(client) ||
    includesAny(dataSource, CHAT_CONTENT_FIRST_DATA_SOURCE_FRAGMENTS)
  );
}

function isPdfContentFirstSource(dataSource: string, client: string): boolean {
  return (
    PDF_CONTENT_FIRST_DATA_SOURCES.has(dataSource) ||
    PDF_CONTENT_FIRST_CLIENTS.has(client) ||
    includesAny(dataSource, PDF_CONTENT_FIRST_DATA_SOURCE_FRAGMENTS)
  );
}

function shouldRenderAsHtml(dataSource: string, hasStructuredHtml: boolean): boolean {
  return hasStructuredHtml || includesAny(dataSource, HTML_RENDER_DATA_SOURCE_FRAGMENTS);
}

function includesAny(value: string, fragments: readonly string[]): boolean {
  return fragments.some((fragment) => value.includes(fragment));
}

export interface CitationRouteInfo {
  route: CitationSourceRoute;
  dataSource: string | undefined;
  client: string | undefined;
  rawLink: string;
  isPrivateGcpFile: boolean;
  isPdf: boolean;
  hasIframeHtml: boolean;
  hasStructuredHtml: boolean;
}

export function getCitationRouteInfo(item: SourceItem): CitationRouteInfo {
  if (item.kind === "table") {
    return {
      route: "table",
      dataSource: item.metadata?.dataSource,
      client: item.client,
      rawLink: "",
      isPrivateGcpFile: false,
      isPdf: false,
      hasIframeHtml: false,
      hasStructuredHtml: false,
    };
  }

  const document = item;
  const dataSource = document.link.metadata?.dataSource ?? document.envelope.metadata?.dataSource;
  const client = document.client;
  const rawLink = document.link.link || "";
  const previewLink = document.link.originalPdfUrl || rawLink;
  const linkText = document.link.text || "";
  const ds = normalize(dataSource);
  const cl = normalize(client);
  const isPrivate = Boolean(previewLink && isPrivateGcpFileUrl(previewLink));
  const isPdf = isPdfLikeUrl(previewLink);
  const hasPdfHighlights = Boolean(document.link.highlights?.length);
  const hasIframeHtml = IFRAME_TAG_RE.test(linkText);
  const hasStructuredHtml = HTML_STRUCTURAL_TAG_RE.test(linkText);
  const hasRenderableContent = linkText.trim().length > 0;
  const baseRouteInfo = {
    dataSource,
    client,
    rawLink: previewLink,
    isPrivateGcpFile: isPrivate,
    isPdf,
    hasIframeHtml,
    hasStructuredHtml,
  };

  if (isPdf && hasRenderableContent && isPdfContentFirstSource(ds, cl)) {
    return {
      ...baseRouteInfo,
      route: "contentWithPdf",
    };
  }

  if (hasIframeHtml) {
    return {
      ...baseRouteInfo,
      route: "iframeHtml",
    };
  }

  if (hasPdfHighlights || isPdf) {
    return {
      ...baseRouteInfo,
      route: "privatePdf",
    };
  }

  if (isChatContentFirstSource(ds, cl)) {
    return routeContent(baseRouteInfo);
  }

  if (isPrivate) {
    return {
      ...baseRouteInfo,
      route: "privateDocument",
      isPrivateGcpFile: true,
    };
  }

  if (shouldRenderAsHtml(ds, hasStructuredHtml)) {
    return {
      ...baseRouteInfo,
      route: "html",
    };
  }

  return {
    ...baseRouteInfo,
    route: "markdown",
  };
}

const PDF_URL_RE = /\.pdf(?:[?#]|$)/i;
const IFRAME_TAG_RE = /<iframe\b/i;

export const HTML_STRUCTURAL_TAG_RE =
  /<(p|h[1-6]|a\s|br\s*\/?>|ul|ol|li|div|blockquote|table|figure)\b/i;

const CHAT_CONTENT_FIRST_DATA_SOURCES = new Set([
  "sec",
  "transcripts",
  "factset streetaccount news",
  "factset amr",
  "user files",
  "adia crm documents",
  "adia investment proposal documents",
  "adia audited financial statements",
  "third bridge transcripts",
  "pitchbook",
  "crunchbase",
  "visible alpha",
  "capiq estimates",
  "capiq fundamentals",
  "the web",
  "company website",
  "investor relations documents",
  "investor relations",
  "investor documents",
  "ir documents",
  "industry research",
  "factset fundamentals",
  "factset segment-level data",
  "factset consensus estimates",
  "factset global share price trend data",
  "factset earnings surprise data",
  "upcoming event dates",
  "earnings calendar",
  "factset company profiles",
  "historic event dates",
  "factset people executives",
  "factset people profile",
  "factset m&a deals",
  "factset etf reference",
  "factset private company profile",
  "factset private company financials",
  "factset ownership fund holdings",
  "factset ownership security holders",
  "factset ownership insider transactions",
  "factset ownership institutional activity",
  "factset economic events",
  "factset index benchmark",
  "factset rbics entity focus",
  "factset rbics taxonomy structure",
  "factset rbics entity revenue",
  "factset rbics industry focus",
  "factset rbics industry revenue",
  "factset rbics trade names",
]);

const CHAT_CONTENT_FIRST_DATA_SOURCE_FRAGMENTS = ["factset rbics"];

const CHAT_CONTENT_FIRST_CLIENTS = new Set([
  "get_sec_data",
  "get_transcripts_data",
  "get_web_data",
  "get_company_website_data",
  "get_investor_relations_data",
  "get_investor_relations_documents_data",
  "get_ir_documents_data",
  "get_industry_research_data",
  "get_factset_data",
  "get_estimates_data",
  "get_global_price_trend_v2",
]);

const PDF_CONTENT_FIRST_DATA_SOURCES = new Set([
  "investor relations documents",
  "investor relations",
  "investor documents",
  "ir documents",
]);

const PDF_CONTENT_FIRST_DATA_SOURCE_FRAGMENTS = [
  "investor relations",
  "investor document",
  "ir document",
];

const PDF_CONTENT_FIRST_CLIENTS = new Set([
  "get_investor_relations_data",
  "get_investor_relations_documents_data",
  "get_ir_documents_data",
]);

const HTML_RENDER_DATA_SOURCE_FRAGMENTS = ["website", "web", "industry"];
